import React,{ useEffect, useState, useCallback } from "react"
import type { FormInstance } from "antd/lib/form"
import { Radio, Row, Col, Form } from "antd"
import Title from "components/Title"
import GetLabel from "DisplayFramework/components/GetLabel"
import FormInput from "components/FormInput"
import ProductInput from "pages/ProposalV3/PropDetail/ProductInput"
import OfferInput from "pages/ProposalV3/PropDetail/OfferInput"
import type { AssetRVCriteria, AssetUseCriteria, FilteringCriteria, prFilteringCriteria } from "services/pricing"
import { sumBy } from "lodash"
import getEnvValue from "utils/getEnvValue"
import ProductConversionRuleListSelect from "components/ProductConversionRuleListSelect"

export type ProductSortieProps = {
  form: FormInstance
  required: boolean
  sortieData: {
    isAutomaticConversion?: boolean
    isCapitalRepayment?: boolean
    offeridtarget?: number
    targetprcode?: string
    offerlabel?: string
    prcode:string
  }
  setSortieData: (data: any) => void
}

const ProductSortie = ({ form, required, sortieData, setSortieData }: ProductSortieProps) => {
  const [prcode, setprcode] = useState<string>(sortieData?.targetprcode || "")
  const [selectedOption, setSelectedOption] = useState<boolean | null>(sortieData?.isAutomaticConversion ?? null)
  const [offers, setOffers] = useState<any[]>([])

  // Déplacer getFilterList avant son utilisation
  const getFilterList = (_assets?: any[], _offerCriteriaList?: Array<FilteringCriteria>): prFilteringCriteria => {
    const assetList = _assets ?? form?.getFieldValue("assets") ?? []

    const assetUseCriteriaList: Array<AssetUseCriteria> = []
    const assetRVCriteriaList: Array<AssetRVCriteria> = []
    const offerCriteriaList: Array<FilteringCriteria> = _offerCriteriaList ?? []
    let ret: prFilteringCriteria

    offerCriteriaList.push({
      critcode: "PROPORIGIN",
      critvalue: form?.getFieldValue("ctproporigin") ?? "",
    })

    offerCriteriaList.push({
      critcode: "DEALRATING",
      critvalue: form?.getFieldValue("ctdealerrating") ?? "",
    })

    offerCriteriaList.push({
      critcode: "OFFER",
      critvalue: form?.getFieldValue("offerid") ?? "0",
    })
    offerCriteriaList.push({
      critcode: "NASSETS",
      critvalue: form?.getFieldValue("assets")?.length ?? "0",
    })

    const duration = form
      ?.getFieldValue(["elements", "FIN"])
      ?.filter((e: any) => e?.cteid === form?.getFieldValue("selectedElement"))[0]?.cteduration

    offerCriteriaList.push({
      critcode: "DURATION",
      critvalue: duration ?? 48,
    })

    const invest = sumBy(form?.getFieldValue("assets"), "assetnetamount")

    offerCriteriaList.push({
      critcode: "INVEST",
      critvalue: String(invest) ?? "0",
    })

    offerCriteriaList.push({
      critcode: "PRCODE",
      critvalue: form?.getFieldValue("flagmasterfacility")
        ? form?.getFieldValue("mfprcode")
        : (form?.getFieldValue("prcode") ?? ""),
    })
    offerCriteriaList.push({
      critcode: "TPTYPE",
      critvalue: form.getFieldValue("client")?.tptype ?? "",
    })
    offerCriteriaList.push({
      critcode: "NETWORK",
      critvalue: form.getFieldValue("ctnetworkcode") ?? "",
    })
    offerCriteriaList.push({
      critcode: "CLIRATING",
      critvalue: form.getFieldValue("client")?.tpsalestatus ?? "",
    })

    assetList?.forEach((a: any) => {
      assetRVCriteriaList.push({
        catalogid: a.catalogid,
        assetid: a.assetid,
        assetsegment: a.assetsegment,
        assetfamily: a.assetfamily,
        assettype: a.assettype,
        assetcategory: a.assetcategory,
        assetmodel: a.assetmodel,
        assetbrand: a.assetbrand,
        assetbrandmodel: a.assetbrandmodel,
        assetnetamount: a.assetnetamount,
        assetfinamount: a.assetfinamount,
        assetvariant: a.assetvariant,
      })

      offerCriteriaList.push({
        critcode: "ASSETFAM",
        critvalue: a.assetfamily,
      })
      offerCriteriaList.push({ critcode: "ASSETTYPE", critvalue: a.assettype })
      offerCriteriaList.push({
        critcode: "ASSETCAT",
        critvalue: a.assetcategory,
      })

      offerCriteriaList.push({
        critcode: "ASSETMODEL",
        critvalue: a.assetmodel,
      })

      a.assetusereqList?.forEach((u: any) => {
        offerCriteriaList.push({
          critcode: u.attcode,
          critvalue: u.usevalue ?? "",
        })
      })

      offerCriteriaList.push({
        critcode: "FLAGUSED",
        critvalue: String(a.flagused) ?? "",
      })

      a.power != null && offerCriteriaList.push({ critcode: "POWER", critvalue: a.power ?? "" })

      a.entityAddAttributList?.forEach((e: any) => {
        e.attstringvalue !== null
          ? offerCriteriaList.push({
              critcode: e.attcode,
              critvalue: e.attstringvalue,
            })
          : e.attnumericvalue !== null
            ? offerCriteriaList.push({
                critcode: e.attcode,
                critvalue: e.attnumericvalue,
              })
            : null
        offerCriteriaList.push({
          critcode: e.attcode,
          critvalue: e.attnumericvalue,
        })
      })

      if (getEnvValue("FLAG_APPLY_ASSET_USE")) {
        a.assetusereqList?.forEach((u: any) => {
          assetUseCriteriaList.push({
            assetfamily: a.assetfamily,
            assettype: a.assettype,
            assetcategory: a.assetcategory,
            catalogid: a.catalogid ?? "",
            attcode: u.attcode ?? "",
            attvalue: u.usevalue ?? "",
          })
        })
      }
    })

    ret = {
      prcode: form.getFieldValue("targetprcode") ?? '',
      tpid: form.getFieldValue("client")?.tpid ?? 0,
      tpidbroker: form.getFieldValue("broker")?.tpid ?? 0,
      tpidmanufacturer: assetList && assetList.length > 0 ? (assetList![0]?.tpidmanufacturer ?? 0) : 0,
      assetUseCriteria: assetUseCriteriaList,
      assetRVCriteria: assetRVCriteriaList,
      offerCriteria: offerCriteriaList,
      flagApplyAssetUse: getEnvValue("FLAG_APPLY_ASSET_USE"),
      flagApplyAttributesExtendDurationValues: true,
      flagApplyAttributesExtendFirstRentalValues: true,
      flagApplyAttributesExtendRVValues: true,
      flagApplyAttributesExtendRateValues: true,
      flagApplyExtendDurationValues: true,
      flagApplyExtendFirstRentalValues: true,
      flagApplyExtendRVValues: true,
      flagApplyExtendRateValues: true,
    }
    return ret
  }
  // Initialize form values when sortieData changes
  useEffect(() => {
    if (sortieData) {
      form.setFieldsValue({
        isAutomaticConversion: sortieData.isAutomaticConversion,
        isCapitalRepayment: sortieData.isCapitalRepayment,
        offeridtarget: sortieData.offeridtarget,
        targetprcode: sortieData.targetprcode,
      })

      setSelectedOption(sortieData.isAutomaticConversion ?? null)
      setprcode(sortieData.targetprcode || "")
    }
  }, [sortieData, form])

  // Callback pour gérer les offres chargées
  const handleOffersLoaded = useCallback((offers: any[]) => {
    setOffers(offers);
  }, []);

  // Memoized function to update sortie data
  const updateSortieData = useCallback(
    (updates: Partial<typeof sortieData>) => {
      const newSortieData = {
        ...sortieData,
        ...updates,
      }
      setSortieData(newSortieData)
    },
    [sortieData, setSortieData],
  )

  const handleRadioChange = (e: any) => {
    const isAuto = e.target.value
    setSelectedOption(isAuto)

    const updates = {
      isAutomaticConversion: isAuto,
      isCapitalRepayment: !isAuto,
    }

    form.setFieldsValue(updates)
    updateSortieData(updates)
  }

  // Handle form field changes
  const handleFieldChange = (changedFields: any, allFields: any) => {
    const formValues = form.getFieldsValue()

    const relevantFields = ["isAutomaticConversion", "isCapitalRepayment", "targetprcode", "offeridtarget"]
    const hasRelevantChanges = changedFields.some((field: any) => relevantFields.includes(field.name[0]))

    if (hasRelevantChanges) {
      const updates = {
        isAutomaticConversion: formValues.isAutomaticConversion,
        isCapitalRepayment: formValues.isCapitalRepayment,
        targetprcode: formValues.targetprcode,
        offeridtarget: formValues.offeridtarget,
      }
      updateSortieData(updates)
    }
  }

  // Handle offer selection
  const handleOfferChange = (offerId: number) => {
    const selectedOffer = offers.find(offer => offer.offerid === offerId);
    
    updateSortieData({
      offeridtarget: offerId,
      offerlabel: selectedOffer?.offerlabel || ""
    });
  };

  return (
    <Form form={form} onFieldsChange={handleFieldChange}>
      <div className="container-stepper mt-4 taux productlist">
        <Title comcode="infoToCompleteSortie" level={4} className="ut-updateTP-title mb-7 mt-10" />
        <Row className="mb-4" gutter={24} justify="space-between" align="middle">
          <Col span={24}>
            <FormInput
              name="isAutomaticConversion"
              nolabel
              className="color-gray mb-7"
            >
              <Radio.Group onChange={handleRadioChange} value={selectedOption} className="flex-column d-flex row">
                <Radio value={false} className="mt-7 col-24">
                  {" "}
                  <GetLabel label="capitalRepay" /> <GetLabel label="resttopay" /> {"("}
                  {form.getFieldValue("durationmax")} <GetLabel label="days" />
                  {")"}
                </Radio>

                <Radio value className="mt-7 mb-7 ant-col-12 ant-col">
                  <GetLabel label="autoConversion" />
                </Radio>
              </Radio.Group>
            </FormInput>
          </Col>
        </Row>

        {selectedOption === true && (
          <Row style={{ marginTop: "-16px" }} gutter={24} justify="center" align="middle">
            <Col span={7}>
              <FormInput
                name="targetprcode"
                rules={[
                  {
                    required: required,
                    message: "Veuillez entrer la famille",
                  },
                ]}
                className="color-gray mb-7"
                nolabel
              >
             
                 <ProductConversionRuleListSelect
                params={{
                  sourcePrcode: form.getFieldValue('prcode'),
                  targetPrcode: "",
                  isAuthorized: true,
                  page: undefined,
                  size: undefined,
                  sortBy: '',
                  sortDirection: '',
                }}
                onSelected={(value:any) => {
                   setprcode(value.targetPrcode)
                    updateSortieData({ targetprcode: value.targetPrcode})
                }}
                onChange={(value:any) => { form.setFieldsValue({ offerIdTarget: "" });}}
              />
              </FormInput>
            </Col>
            <Col span={7}>
              <FormInput
                nolabel
                name="offeridtarget"
                rules={[
                  {
                    required: required,
                    message: "Veuillez entrer le code",
                  },
                ]}
                className="color-gray mb-7"
              >
                <OfferInput
                  disabled={!form.getFieldValue("prcode")}
                  params={{
                    prcode: form.getFieldValue("flagmasterfacility")
                      ? form.getFieldValue("mfprcode")
                      : form.getFieldValue("prcode") || "",
                    filtersList: getFilterList(), // Maintenant accessible
                    filters: "",
                    finamount: form.getFieldValue("ctfinancedamount"),
                    duration: form.getFieldValue("cteduration"),
                    searchMode: "PRCODE",
                  }}
                  onOffersLoaded={handleOffersLoaded}
                  onChange={handleOfferChange}
                />
              </FormInput>
            </Col>
          </Row>
        )}
      </div>
    </Form>
  )
}

export default ProductSortie